import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Lightweight autoplaying slideshow for the heroes: the classic hero (image slides)
 * and the block-based Hero (sections/hero-slides, slides with their own copy —
 * notes/hero.md).
 *
 * Expects children with `ref="slides[]"`; optional `ref="indicators[]"` buttons
 * (progress lines) jump to their slide and mirror the active one.
 *
 * Rotation holds while the pointer is over the hero or keyboard focus is inside it,
 * and while the tab is hidden; it carries on from where it stopped, so a progress
 * line (a CSS animation paused by [data-holding]) stays in step. Slides that aren't
 * showing are `inert`, so their links can't be tabbed to. In the theme editor,
 * selecting a slide block shows that slide and holds there.
 *
 * Attributes:
 * - autoplay: number (seconds)
 */
export class HeroSlideshow extends Component {
  /** @type {string[]} */
  requiredRefs = ['slides'];

  /** @type {number | undefined} */
  #timer = undefined;

  /** Time left on the current slide when rotation was held (ms). */
  /** @type {number | undefined} */
  #remaining = undefined;

  /** @type {number} */
  #startedAt = 0;

  /** @type {number} */
  #index = 0;

  /** Reasons rotation is held: 'hover', 'focus', 'hidden', 'editor'. */
  /** @type {Set<string>} */
  #holds = new Set();

  /** @type {number | undefined} */
  #pendingAnimationFrame = undefined;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.#slides;
    if (!slides.length) return;

    // Start on the slide the server marked active (the first one this visitor gets).
    const marked = slides.findIndex((slide) => slide.hasAttribute('data-active'));
    this.#index = marked >= 0 ? marked : 0;
    this.#apply({ animate: false });

    if (slides.length <= 1) return;

    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.addEventListener('click', this.#handleIndicatorClick);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);

    this.resume();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimer();
    this.#cancelPendingAnimation();

    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.removeEventListener('click', this.#handleIndicatorClick);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  get #slides() {
    const slides = this.refs.slides;
    return Array.isArray(slides) ? slides : [];
  }

  get #indicators() {
    const indicators = this.refs.indicators;
    return Array.isArray(indicators) ? indicators : [];
  }

  get autoplayInterval() {
    const raw = this.getAttribute('autoplay');
    const seconds = parseInt(`${raw ?? ''}`, 10);
    if (Number.isNaN(seconds) || seconds <= 0) return undefined;
    return seconds * 1000;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) this.setAttribute('paused', '');
    else this.removeAttribute('paused');
  }

  pause = () => {
    this.paused = true;
    this.#clearTimer();
  };

  resume = () => {
    if (!this.autoplayInterval || this.paused || this.#holds.size) return;
    this.#schedule(this.#remaining ?? this.autoplayInterval);
    this.#remaining = undefined;
  };

  next = () => {
    const slides = this.#slides;
    if (slides.length <= 1) return;
    this.goTo((this.#index + 1) % slides.length);
  };

  /**
   * Show slide `index` and give it a full duration.
   * @param {number} index
   */
  goTo(index) {
    const slides = this.#slides;
    if (!slides.length) return;
    this.#index = Math.min(Math.max(0, index), slides.length - 1);
    this.#remaining = undefined;
    this.#clearTimer();
    this.#apply({ animate: true });
    this.resume();
  }

  /** @param {number} ms */
  #schedule(ms) {
    this.#clearTimer();
    this.#startedAt = Date.now() - (this.autoplayInterval ?? ms) + ms;
    this.#timer = window.setTimeout(() => {
      this.#timer = undefined;
      this.next();
    }, ms);
  }

  #clearTimer() {
    if (this.#timer === undefined) return;
    window.clearTimeout(this.#timer);
    this.#timer = undefined;
  }

  /** @param {string} reason */
  #hold(reason) {
    const wasRunning = this.#holds.size === 0;
    this.#holds.add(reason);
    this.toggleAttribute('data-holding', true);
    if (wasRunning && this.#timer !== undefined && this.autoplayInterval) {
      const elapsed = Date.now() - this.#startedAt;
      this.#remaining = Math.max(0, this.autoplayInterval - elapsed);
      this.#clearTimer();
    }
  }

  /** @param {string} reason */
  #release(reason) {
    if (!this.#holds.delete(reason)) return;
    if (this.#holds.size) return;
    this.removeAttribute('data-holding');
    this.resume();
  }

  #cancelPendingAnimation() {
    if (this.#pendingAnimationFrame) {
      cancelAnimationFrame(this.#pendingAnimationFrame);
      this.#pendingAnimationFrame = undefined;
    }
  }

  #setActive() {
    for (const [i, slide] of this.#slides.entries()) {
      const isActive = i === this.#index;
      slide.toggleAttribute('data-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
    }
    for (const [i, indicator] of this.#indicators.entries()) {
      const isActive = i === this.#index;
      // Re-adding the attribute restarts the line's fill animation.
      indicator.removeAttribute('data-active');
      if (isActive) {
        void indicator.offsetWidth;
        indicator.setAttribute('data-active', '');
        indicator.setAttribute('aria-current', 'true');
      } else {
        indicator.removeAttribute('aria-current');
      }
    }
  }

  /**
   * @param {{ animate: boolean }} opts
   */
  #apply({ animate }) {
    if (!this.#slides.length) return;

    // Cancel any pending animation frame to prevent race conditions
    this.#cancelPendingAnimation();

    const instant = prefersReducedMotion() || !animate;

    if (instant) {
      this.setAttribute('data-instant-transitions', '');
      this.#setActive();
      // Remove the attribute after a frame to reset state for future transitions
      this.#pendingAnimationFrame = requestAnimationFrame(() => {
        this.removeAttribute('data-instant-transitions');
        this.#pendingAnimationFrame = undefined;
      });
    } else {
      // The removal must land before data-active changes so CSS transitions run.
      this.removeAttribute('data-instant-transitions');
      this.#pendingAnimationFrame = requestAnimationFrame(() => {
        this.#setActive();
        this.#pendingAnimationFrame = undefined;
      });
    }
  }

  #handleMouseEnter = () => this.#hold('hover');
  #handleMouseLeave = () => this.#release('hover');
  #handleFocusIn = () => this.#hold('focus');

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#release('focus');
  };

  #handleVisibilityChange = () => (document.hidden ? this.#hold('hidden') : this.#release('hidden'));

  /** @param {MouseEvent} event */
  #handleIndicatorClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const indicator = target.closest('[ref="indicators[]"]');
    if (!indicator) return;
    const index = this.#indicators.indexOf(/** @type {HTMLElement} */ (indicator));
    if (index >= 0) this.goTo(index);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = /** @type {Element} */ (event.target);
    const index = this.#slides.findIndex((slide) => slide === target || slide.contains(target));
    if (index < 0) return;
    this.#hold('editor');
    this.#index = index;
    this.#apply({ animate: false });
  };

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!this.#slides.some((slide) => slide === target || slide.contains(target))) return;
    this.#release('editor');
  };
}

if (!customElements.get('hero-slideshow-component')) {
  customElements.define('hero-slideshow-component', HeroSlideshow);
}
